import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Paperclip, Send, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { PickSelect } from "@/components/apply/Field";
import { RichEditor } from "@/components/RichEditor";
import { api, formatErr } from "@/lib/api";

const STATUSES = ["New", "ID Under Review", "Interview Scheduled", "Interviewed", "Approved", "Declined", "Cash Payment Received", "Active Member", "Suspended"];
const TIERS = ["bronze", "silver", "gold", "platinum", "custom"];
const ALL = "__all";
const opts = (l) => [{ value: ALL, label: "Any" }, ...l.map((v) => ({ value: v, label: v[0].toUpperCase() + v.slice(1) }))];
const TAGS = ["first_name", "full_name", "email", "tier", "tier_price", "country", "portal_link"];
const MODES = [{ value: "individual", label: "A single person" }, { value: "selected", label: "Selected people" }, { value: "filter", label: "Everyone matching a filter" }];

const PeoplePicker = ({ people, ids, setIds, single }) => {
  const [q, setQ] = useState("");
  const shown = useMemo(() => people.filter((p) => `${p.contact.full_name} ${p.contact.email}`.toLowerCase().includes(q.toLowerCase())).slice(0, 100), [people, q]);
  const toggle = (id) => setIds(single ? [id] : ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  return (
    <div className="rounded-xl border border-border">
      <Input placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 rounded-none border-0 border-b" data-testid="compose-people-search" />
      <div className="max-h-56 overflow-y-auto p-2 space-y-1">
        {shown.map((p) => (
          <label key={p.id} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-secondary cursor-pointer text-sm" data-testid={`compose-person-${p.id}`}>
            <Checkbox checked={ids.includes(p.id)} onCheckedChange={() => toggle(p.id)} />
            <span className="font-semibold">{p.contact.full_name}</span><span className="text-muted-foreground truncate">{p.contact.email} · {p.status}</span>
          </label>
        ))}
        {shown.length === 0 && <p className="text-sm text-muted-foreground p-2">No matches.</p>}
      </div>
    </div>
  );
};

export const ComposeEmail = ({ onSent }) => {
  const [people, setPeople] = useState({ items: [], countries: [] });
  const [mode, setMode] = useState("individual");
  const [ids, setIds] = useState([]);
  const [filters, setFilters] = useState({ status: "", tier: "", country: "" });
  const [preview, setPreview] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ver, setVer] = useState(0);

  useEffect(() => {
    api.get("/admin/applications").then((r) => setPeople(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (mode !== "filter") return;
    api.post("/admin/emails/recipients-preview", { mode, filters }).then((r) => setPreview(r.data)).catch(() => setPreview(null));
  }, [mode, filters]);

  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v === ALL ? "" : v }));
  const count = mode === "filter" ? preview?.count ?? 0 : ids.length;

  const send = async () => {
    if (!subject.trim() || !body.replace(/<[^>]+>/g, "").trim()) return toast.error("Add a subject and message.");
    if (!count) return toast.error("Choose at least one recipient.");
    if (!window.confirm(`Send this email to ${count} recipient(s)?`)) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("subject", subject);
    fd.append("body", body);
    fd.append("mode", mode);
    fd.append("ids", JSON.stringify(ids));
    fd.append("filters", JSON.stringify(filters));
    files.forEach((f) => fd.append("attachments", f));
    try {
      const { data } = await api.post("/admin/emails/compose", fd);
      toast.success(`Queued ${data.sent} email(s)`);
      setSubject("");
      setBody("");
      setFiles([]);
      setIds([]);
      setVer((v) => v + 1);
      onSent();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,380px)_1fr]" data-testid="compose-email">
      <div className="space-y-4 rounded-2xl border border-border bg-card p-5">
        <p className="font-bold">Recipients</p>
        <PickSelect value={mode} onChange={(v) => { setMode(v); setIds([]); }} options={MODES} testId="compose-mode-select" />
        {mode === "filter" ? (
          <div className="space-y-3">
            <PickSelect value={filters.status || ALL} onChange={setF("status")} options={opts(STATUSES)} placeholder="Status" testId="compose-filter-status" />
            <PickSelect value={filters.tier || ALL} onChange={setF("tier")} options={opts(TIERS)} placeholder="Tier" testId="compose-filter-tier" />
            <PickSelect value={filters.country || ALL} onChange={setF("country")} options={opts(people.countries)} placeholder="Country" testId="compose-filter-country" />
            {preview?.count === 500 && <p className="text-xs text-amber-500">Limited to 500 recipients per send.</p>}
          </div>
        ) : (
          <PeoplePicker people={people.items} ids={ids} setIds={setIds} single={mode === "individual"} />
        )}
        <p className="text-sm font-bold" data-testid="compose-recipient-count">{count} recipient(s)</p>
      </div>
      <div className="space-y-4 rounded-2xl border border-border bg-card p-5">
        <Input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="h-11 rounded-xl" data-testid="compose-subject-input" />
        <RichEditor key={ver} value={body} onChange={setBody} tags={TAGS} testId="compose-editor" minHeight={240} />
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex h-10 items-center gap-2 px-4 rounded-full border border-border font-semibold text-sm hover:bg-secondary cursor-pointer" data-testid="compose-attach-button">
            <Paperclip className="h-4 w-4" />Attach files
            <input type="file" multiple className="hidden" data-testid="compose-attach-input" onChange={(e) => { setFiles((f) => [...f, ...Array.from(e.target.files || [])]); e.target.value = ""; }} />
          </label>
          {files.map((f, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs font-semibold">{f.name}
              <button onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label="Remove"><X className="h-3 w-3" /></button></span>
          ))}
        </div>
        <button onClick={send} disabled={busy} className="inline-flex h-11 items-center gap-2 px-6 rounded-full bg-primary text-primary-foreground font-bold btn-glow disabled:opacity-60" data-testid="compose-send-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send email
        </button>
      </div>
    </div>
  );
};
